import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  Briefcase,
  Building2,
  Check,
  FileSignature,
  Landmark,
  LifeBuoy,
  Lock,
  Menu,
  Receipt,
  Repeat,
  ScrollText,
  ShieldCheck,
  Users,
  X,
} from 'lucide-react';
import { Brand, PRODUCT_NAME } from '@/components/shared/brand';
import { useSession } from '@/features/auth/use-session';
import { PHASES, shippedPhases } from '@/lib/phases';
import { cn } from '@/lib/utils';

const NAV = [
  ['#modules', 'Modules'],
  ['#tour', 'Product tour'],
  ['#control', 'Access control'],
  ['#roadmap', 'Roadmap'],
] as const;

/**
 * The signed-out front door.
 *
 * Everything claimed on this page is a screen that exists. A landing page that
 * promises modules the product has not built yet is a support ticket waiting to
 * happen, so the roadmap section says plainly what is live and what is not.
 */
export function HomePage() {
  const { data: user } = useSession();
  const [menuOpen, setMenuOpen] = useState(false);

  const signedIn = Boolean(user);
  const ctaLabel = signedIn ? 'Go to dashboard' : 'Sign in';
  const ctaTo = signedIn ? '/dashboard' : '/login';

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/85 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-6 px-6">
          <Link to="/" aria-label={PRODUCT_NAME}>
            <Brand size="sm" />
          </Link>

          <nav className="hidden items-center gap-8 md:flex">
            {NAV.map(([href, label]) => (
              <a
                key={href}
                href={href}
                className="text-sm text-slate-600 transition-colors hover:text-slate-900"
              >
                {label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to={ctaTo}
              className="hidden items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/25 transition-colors hover:bg-red-700 sm:flex"
            >
              {ctaLabel}
              <ArrowRight className="h-4 w-4" />
            </Link>
            <button
              className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 md:hidden"
              onClick={() => setMenuOpen((open) => !open)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="border-t border-slate-200 bg-white px-6 py-4 md:hidden">
            <nav className="flex flex-col gap-1">
              {NAV.map(([href, label]) => (
                <a
                  key={href}
                  href={href}
                  onClick={() => setMenuOpen(false)}
                  className="rounded-lg px-2 py-2.5 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                >
                  {label}
                </a>
              ))}
              <Link
                to={ctaTo}
                className="mt-2 flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground"
              >
                {ctaLabel}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </nav>
          </div>
        )}
      </header>

      <Hero ctaLabel={ctaLabel} ctaTo={ctaTo} />
      <Modules />
      <Tour />
      <AccessControl />
      <Roadmap />
      <ClosingCta ctaLabel={ctaLabel} ctaTo={ctaTo} />
      <Footer />
    </div>
  );
}

function Hero({ ctaLabel, ctaTo }: { ctaLabel: string; ctaTo: string }) {
  return (
    <section className="relative overflow-hidden border-b border-slate-200">
      <div className="bg-grid absolute inset-0" aria-hidden />
      <div
        className="absolute -top-28 left-1/2 h-[420px] w-[820px] -translate-x-1/2 rounded-full bg-primary/[0.07] blur-[120px]"
        aria-hidden
      />

      <div className="relative mx-auto max-w-6xl px-6 pb-24 pt-20 text-center">
        <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-medium text-slate-600 shadow-sm">
          <Landmark className="h-3.5 w-3.5 text-primary" />
          Built for the way this business actually runs
        </span>

        <h1 className="mx-auto mt-7 max-w-4xl text-balance text-[2.75rem] font-extrabold leading-[1.06] tracking-tight sm:text-6xl">
          One system from first contact to final payment
        </h1>

        <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-slate-600">
          Leads, companies, pipeline, quotes, invoices, payments, expenses and a
          double-entry ledger — three revenue lines in one place, with a permission model
          that decides who sees what.
        </p>

        <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
          <Link
            to={ctaTo}
            className="flex items-center gap-2 rounded-lg bg-primary px-6 py-3.5 text-[0.9375rem] font-semibold text-primary-foreground shadow-lg shadow-primary/25 transition-colors hover:bg-red-700"
          >
            {ctaLabel}
            <ArrowRight className="h-4 w-4" />
          </Link>
          <a
            href="#modules"
            className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-6 py-3.5 text-[0.9375rem] font-semibold text-slate-700 transition-colors hover:border-slate-400 hover:bg-slate-50"
          >
            <ScrollText className="h-4 w-4 text-slate-400" />
            See what is inside
          </a>
        </div>

        <DashboardMock />
      </div>
    </section>
  );
}

/**
 * A still of the real finance dashboard. Hand-built rather than a screenshot so
 * it stays sharp, themes with the rest of the page, and never goes stale in a
 * way nobody notices.
 */
function DashboardMock() {
  const stages = [
    ['Qualified', 34, '2.10M'],
    ['Proposal', 58, '3.60M'],
    ['Negotiation', 76, '1.85M'],
    ['Won', 100, '4.85M'],
  ] as const;

  return (
    <div className="shadow-brand mx-auto mt-16 max-w-4xl overflow-hidden rounded-2xl border border-slate-200 bg-white text-left">
      <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-4 py-3">
        <span className="flex gap-1.5" aria-hidden>
          <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
          <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
          <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
        </span>
        <span className="ml-2 rounded border border-slate-200 bg-white px-2.5 py-1 text-[11px] text-slate-500">
          orostarportal.com/finance
        </span>
      </div>

      <div className="grid gap-px bg-slate-200 sm:grid-cols-3">
        {[
          ['Collected this month', 'AED 1.24M', '+18% on last month'],
          ['Outstanding', 'AED 486K', '12 invoices, 3 overdue'],
          ['Recurring revenue', 'AED 96K', 'MRR across 41 subscriptions'],
        ].map(([label, value, hint]) => (
          <div key={label} className="bg-white px-5 py-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
              {label}
            </p>
            <p className="tabular mt-1.5 text-2xl font-extrabold tracking-tight">{value}</p>
            <p className="mt-1 text-xs text-slate-500">{hint}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-px border-t border-slate-200 bg-slate-200 sm:grid-cols-2">
        <div className="bg-white px-5 py-5">
          <p className="text-sm font-semibold">Pipeline by stage</p>
          <ul className="mt-4 space-y-3">
            {stages.map(([name, width, value]) => (
              <li key={name}>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-600">{name}</span>
                  <span className="tabular text-slate-500">AED {value}</span>
                </div>
                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full bg-primary" style={{ width: `${width}%` }} />
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white px-5 py-5">
          <p className="text-sm font-semibold">Needs attention</p>
          <ul className="mt-4 space-y-3">
            {[
              ['INV-2026-00184', 'Overdue 22 days', 'AED 62,400'],
              ['INV-2026-00177', 'Awaiting approval', 'AED 18,900'],
              ['SUB-2026-00042', 'Renews in 7 days', 'AED 4,200'],
            ].map(([ref, note, amount]) => (
              <li key={ref} className="flex items-center justify-between gap-3 text-xs">
                <span className="min-w-0">
                  <span className="block truncate font-medium text-slate-700">{ref}</span>
                  <span className="block text-slate-500">{note}</span>
                </span>
                <span className="tabular shrink-0 font-semibold">{amount}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

const MODULES = [
  {
    icon: Users,
    title: 'Leads and contacts',
    body: 'Every enquiry in one list, with fuzzy duplicate detection that catches "MERIDIAN LOGISTIC LLC" against an existing "Meridian Logistics" before you create the second record.',
  },
  {
    icon: Briefcase,
    title: 'Pipeline and deals',
    body: 'A drag-and-drop board over stages you define. A stage can require fields before a deal enters it, and a loss always asks why.',
  },
  {
    icon: FileSignature,
    title: 'Quotes with a client link',
    body: 'Send a link, not an attachment. The client accepts or declines in their browser, signs, and the quote locks itself the moment they do.',
  },
  {
    icon: Receipt,
    title: 'Invoices',
    body: 'Approval is the point of no return: the number is allocated, the ledger is posted and the document freezes. Drafts carry placeholders, so deleting one leaves no gap in the sequence.',
  },
  {
    icon: Landmark,
    title: 'Payments and allocation',
    body: 'Record one payment against several invoices, oldest first. Anything over the balance is held as unallocated rather than quietly overpaying a bill.',
  },
  {
    icon: Repeat,
    title: 'Subscriptions',
    body: 'Products, plans and versions. A price change makes a new plan version, so a client already on the old one keeps the price they agreed.',
  },
  {
    icon: BarChart3,
    title: 'Reports off the ledger',
    body: 'Receivables ageing, profit and loss, and a trial balance that has to balance. Every figure traces back to a posting, because none of them are typed in.',
  },
  {
    icon: LifeBuoy,
    title: 'Expenses',
    body: 'Submit, approve, post. Rejecting an already-approved expense reverses the entry rather than editing history.',
  },
] as const;

function Modules() {
  return (
    <section id="modules" className="border-b border-slate-200 bg-slate-50 py-24">
      <div className="mx-auto max-w-6xl px-6">
        <p className="eyebrow">Modules</p>
        <h2 className="mt-3 max-w-2xl text-4xl font-extrabold tracking-tight">
          Everything in one platform
        </h2>
        <p className="mt-4 max-w-2xl text-lg text-slate-600">
          No spreadsheet handovers between sales and accounts. The deal that was won is the
          invoice that gets raised.
        </p>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {MODULES.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="rounded-xl border border-slate-200 bg-white p-6 transition-shadow hover:shadow-brand"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Icon className="h-5 w-5" />
              </span>
              <h3 className="mt-4 text-base font-bold tracking-tight">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const TOUR = [
  {
    eyebrow: 'Sales',
    title: 'The pipeline tells you the truth',
    body: 'Each column carries its own count, its raw value and its probability-weighted value. Moving a card records who moved it, when, and how long it sat in the stage it left.',
    points: [
      'Stages, probabilities and required fields you define',
      'Weighted and unweighted value per column',
      'Every stage change kept, with its duration',
    ],
  },
  {
    eyebrow: 'Finance',
    title: 'Money that adds up, to the fils',
    body: 'Amounts are held as whole minor units and rounded once per line, so a printed invoice always adds up to its own total. Rounding the sum instead is how a document ends up disagreeing with itself by a fils.',
    points: [
      'Double-entry ledger, append-only',
      'Corrections post a reversal, never an edit',
      'Trial balance that has to balance',
    ],
  },
  {
    eyebrow: 'Clients',
    title: 'One link, no attachment',
    body: 'Quotes and invoices get a private link. The client opens it in a browser on any device, and the owner is told the moment it is read.',
    points: [
      'Accept and sign a quote in the browser',
      'Invoices print cleanly to PDF from the link',
      'You are notified when a client opens one',
    ],
  },
] as const;

function Tour() {
  return (
    <section id="tour" className="border-b border-slate-200 py-24">
      <div className="mx-auto max-w-6xl space-y-20 px-6">
        <div>
          <p className="eyebrow">Product tour</p>
          <h2 className="mt-3 max-w-2xl text-4xl font-extrabold tracking-tight">
            The parts that are easy to get wrong
          </h2>
        </div>

        {TOUR.map((item, index) => (
          <div
            key={item.title}
            className={cn(
              'grid items-center gap-10 lg:grid-cols-2',
              index % 2 === 1 && 'lg:[&>*:first-child]:order-2',
            )}
          >
            <div>
              <p className="eyebrow">
                <span className="mr-2 text-slate-300">
                  {String(index + 1).padStart(2, '0')}
                </span>
                {item.eyebrow}
              </p>
              <h3 className="mt-3 text-3xl font-extrabold tracking-tight">{item.title}</h3>
              <p className="mt-4 text-lg leading-relaxed text-slate-600">{item.body}</p>
              <ul className="mt-6 space-y-2.5">
                {item.points.map((point) => (
                  <li key={point} className="flex items-start gap-2.5 text-[0.9375rem]">
                    <Check className="mt-1 h-4 w-4 shrink-0 text-primary" />
                    <span className="text-slate-700">{point}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="shadow-brand rounded-2xl border border-slate-200 bg-slate-50 p-8">
              <TourPanel index={index} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function TourPanel({ index }: { index: number }) {
  if (index === 0) {
    return (
      <div className="grid grid-cols-3 gap-3">
        {[
          ['Qualified', '6', 'AED 2.1M'],
          ['Proposal', '4', 'AED 3.6M'],
          ['Negotiation', '2', 'AED 1.8M'],
        ].map(([stage, count, value], column) => (
          <div key={stage} className="rounded-lg border border-slate-200 bg-white p-3">
            <p className="text-[11px] font-semibold text-slate-700">{stage}</p>
            <p className="tabular text-[10px] text-slate-500">
              {count} · {value}
            </p>
            <div className="mt-3 space-y-2">
              {Array.from({ length: 3 - column }).map((_, card) => (
                <div key={card} className="rounded-md border border-slate-200 bg-slate-50 p-2">
                  <div className="h-1.5 w-3/4 rounded-full bg-slate-300" />
                  <div className="mt-1.5 h-1.5 w-1/2 rounded-full bg-slate-200" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (index === 1) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between border-b border-slate-200 pb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          <span>Account</span>
          <span className="flex gap-8">
            <span>Debit</span>
            <span>Credit</span>
          </span>
        </div>
        {[
          ['1100 Accounts receivable', '114,450.00', '—'],
          ['4000 Revenue', '—', '109,000.00'],
          ['2100 VAT payable', '—', '5,450.00'],
        ].map(([account, debit, credit]) => (
          <div
            key={account}
            className="flex items-center justify-between border-b border-slate-100 py-2.5 text-xs"
          >
            <span className="text-slate-600">{account}</span>
            <span className="tabular flex gap-6 text-slate-700">
              <span className="w-20 text-right">{debit}</span>
              <span className="w-20 text-right">{credit}</span>
            </span>
          </div>
        ))}
        <div className="flex items-center justify-between pt-2.5 text-xs font-bold">
          <span>Totals</span>
          <span className="tabular flex gap-6">
            <span className="w-20 text-right">114,450.00</span>
            <span className="w-20 text-right">114,450.00</span>
          </span>
        </div>
        <p className="mt-3 flex items-center gap-1.5 text-[11px] font-medium text-emerald-600">
          <Check className="h-3.5 w-3.5" />
          Balanced
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.14em] text-slate-400">Tax invoice</p>
          <p className="text-base font-extrabold tracking-tight">INV-2026-00184</p>
        </div>
        <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold text-emerald-700">
          Viewed by client
        </span>
      </div>
      <div className="mt-4 space-y-2 border-t border-slate-100 pt-4">
        {[
          ['Implementation — phase one', '85,000.00'],
          ['Annual support, 12 months', '24,000.00'],
        ].map(([line, amount]) => (
          <div key={line} className="flex justify-between text-xs">
            <span className="text-slate-600">{line}</span>
            <span className="tabular text-slate-700">{amount}</span>
          </div>
        ))}
        <div className="flex justify-between text-xs">
          <span className="text-slate-500">VAT 5%</span>
          <span className="tabular text-slate-600">5,450.00</span>
        </div>
        <div className="flex justify-between border-t border-slate-200 pt-2 text-sm font-bold">
          <span>Total</span>
          <span className="tabular">AED 114,450.00</span>
        </div>
      </div>
    </div>
  );
}

function AccessControl() {
  return (
    <section id="control" className="border-b border-slate-200 bg-slate-50 py-24">
      <div className="mx-auto grid max-w-6xl items-center gap-12 px-6 lg:grid-cols-2">
        <div>
          <p className="eyebrow">Access control</p>
          <h2 className="mt-3 text-4xl font-extrabold tracking-tight">
            One login. The right slice of it.
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-slate-600">
            Roles are built from a single catalogue of permissions, and each one carries a
            record scope: their own, their team's, or everything. Where two roles overlap the
            wider scope wins, so nobody is accidentally locked out of their own work.
          </p>
          <ul className="mt-7 space-y-3">
            {[
              ['Permissions checked per request', 'Revoking access takes effect immediately, not at next sign-in.'],
              ['Cost figures are their own permission', 'A salesperson can see a deal without seeing its margin.'],
              ['Every change is written down', 'The audit log keeps who changed what, and what it was before.'],
            ].map(([title, body]) => (
              <li key={title} className="flex items-start gap-3">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <span>
                  <span className="block text-[0.9375rem] font-semibold">{title}</span>
                  <span className="block text-sm text-slate-600">{body}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="shadow-brand rounded-2xl border border-slate-200 bg-white p-6">
          <p className="text-sm font-semibold">Sales executive</p>
          <p className="text-xs text-slate-500">Scope: their own records</p>
          <div className="mt-4 space-y-2">
            {[
              ['Leads, companies, contacts', true],
              ['Deals and quotes', true],
              ['Raise an invoice', true],
              ['Approve an invoice', false],
              ['See cost rates and margin', false],
              ['Post to the ledger', false],
            ].map(([label, allowed]) => (
              <div
                key={String(label)}
                className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5 text-sm"
              >
                <span className={allowed ? 'text-slate-700' : 'text-slate-400'}>{label}</span>
                {allowed ? (
                  <Check className="h-4 w-4 text-emerald-600" />
                ) : (
                  <Lock className="h-3.5 w-3.5 text-slate-300" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Roadmap() {
  const allShipped = shippedPhases.length === PHASES.length;

  return (
    <section id="roadmap" className="border-b border-slate-200 py-24">
      <div className="mx-auto max-w-6xl px-6">
        <p className="eyebrow">Roadmap</p>
        <h2 className="mt-3 text-4xl font-extrabold tracking-tight">
          {allShipped
            ? `All ${PHASES.length} phases are live`
            : `${shippedPhases.length} of ${PHASES.length} phases are live`}
        </h2>
        <p className="mt-4 max-w-2xl text-lg text-slate-600">
          {allShipped
            ? 'Everything the specification asks for is built and running — listed here so you can see exactly what that covers.'
            : 'Listed here rather than hidden, because knowing a module has not shipped yet is more useful than finding out by looking for it.'}
        </p>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {PHASES.map(({ name, detail, done }) => (
            <div
              key={name}
              className={cn(
                'rounded-xl border p-6',
                done ? 'border-slate-200 bg-white' : 'border-dashed border-slate-200 bg-slate-50',
              )}
            >
              <div className="flex items-center gap-2">
                {done ? (
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100">
                    <Check className="h-3 w-3 text-emerald-700" />
                  </span>
                ) : (
                  <span className="h-5 w-5 rounded-full border-2 border-slate-200" />
                )}
                <h3
                  className={cn(
                    'text-base font-bold tracking-tight',
                    !done && 'text-slate-400',
                  )}
                >
                  {name}
                </h3>
              </div>
              <p className={cn('mt-2 text-sm', done ? 'text-slate-600' : 'text-slate-400')}>
                {detail}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ClosingCta({ ctaLabel, ctaTo }: { ctaLabel: string; ctaTo: string }) {
  return (
    <section className="relative overflow-hidden bg-sidebar py-24">
      <div
        className="absolute -top-40 left-1/2 h-[420px] w-[820px] -translate-x-1/2 rounded-full bg-primary/25 blur-[120px]"
        aria-hidden
      />
      <div className="relative mx-auto max-w-3xl px-6 text-center">
        <h2 className="text-balance text-4xl font-extrabold tracking-tight text-white">
          Your whole business, behind one login
        </h2>
        <p className="mt-4 text-lg text-white/70">
          Sign in to pick up your pipeline, raise an invoice, or see where the month stands.
        </p>
        <Link
          to={ctaTo}
          className="mt-8 inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3.5 text-[0.9375rem] font-semibold text-primary-foreground shadow-lg shadow-primary/25 transition-colors hover:bg-red-700"
        >
          {ctaLabel}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-slate-200 bg-white py-10">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-6">
        <Brand size="sm" />
        <p className="flex items-center gap-1.5 text-xs text-slate-500">
          <Building2 className="h-3.5 w-3.5" />© {new Date().getFullYear()} {PRODUCT_NAME}
        </p>
      </div>
    </footer>
  );
}
